import React, { useEffect, useState } from "react";
import {
  SupplierItemTraceabilityProps,
  MappedChildRow,
} from "../../types/traceability";
import SourceWipsCard from "../../components/traceability/SourceWipsCard";
import ChildrenTable, {
  mapDirectChildren,
} from "../../components/traceability/ChildrenTable";
import ShippedItemsTable from "../../components/traceability/ShippedItemsTable";

export default function SupplierItemTraceability({
  lookup,
  loading,
  error,
  lotNumber,
  source,
  directChildren,
  loadDirectChildren,
}: SupplierItemTraceabilityProps) {
  const [rows, setRows] = useState<MappedChildRow[]>([]);

  // Update rows when directChildren changes
  useEffect(() => {
    if (directChildren && directChildren.length > 0) {
      setRows(mapDirectChildren(directChildren));
    } else {
      setRows([]);
    }
  }, [directChildren]);

  // Show empty state if no data and not loading
  if (!lotNumber && !source && !directChildren?.length && !loading && !error) {
    return (
      <div className="px-4">
        <div className="card-custom bg-white p-6">
          <p className="text-gray-600">
            Enter a lot number to search for downstream usage.
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !lotNumber && !source && !directChildren?.length) {
    return (
      <div className="px-4">
        <div className="card-custom bg-white p-6">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Source Card */}
      {lotNumber && source && (
        <div className="px-4">
          <SourceWipsCard lotNumber={lotNumber} source={source} />
        </div>
      )}

      {/* Direct Children Table */}
      {(directChildren?.length > 0 || rows.length > 0) && (
        <section className="px-4">
          <div className="card-custom p-2">
            <div className="mb-2">
              <h3 className="text-sm font-medium text-gray-700">
                Direct Children WIPs
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                WIPs that consumed items from lot number {lotNumber || lookup}
              </p>
            </div>
            <ChildrenTable
              level={0}
              rows={rows}
              fetchChildren={loadDirectChildren}
              loading={loading}
              error={error}
            />
          </div>
        </section>
      )}

      {/* Shipped Items Table */}
      {source && !source.is_wip && (source.shipped_items ?? []).length > 0 && (
        <section className="px-4">
          <div className="card-custom p-4 mb-5">
            <div className="sm:flex-auto w-full mb-4">
              <h2 className="text-base font-semibold leading-6 text-gray-900">
                Shipped Items
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Orders that shipped items from lot number {lotNumber || lookup}
              </p>
            </div>
            <ShippedItemsTable items={source.shipped_items ?? []} />
          </div>
        </section>
      )}
    </>
  );
}
